import { ButtonWithPreview } from "@components/Button";
import Image from "@components/_shared/NextImage";
import { useAuth } from "@context/authentication";
import { useSiteConfig } from "@context/siteConfig";
import { useTranslations } from "@context/translations";
import { getDateDiff, GetDateDiffType, UTCtoLocalTime } from "@helpers/date";
import { GenzoCouponCardCtaType } from "@shopcashTypes/genzo/genzoCouponCardCtaType";
import clsx from "clsx";
import React from "react";
import styles from "./CouponCard.module.scss";

interface CouponCardProps {
  deal: StoreDeal;
  onShowCodeClick: (deal: StoreDeal, isLoggedIn: boolean) => void;
  onEventTrack?: (ctaType: GenzoCouponCardCtaType) => void;
}

const CouponCard: React.FC<CouponCardProps> = ({
  deal,
  onShowCodeClick,
  onEventTrack,
}) => {
  const translate = useTranslations();
  const { isRtl } = useSiteConfig();
  const { user } = useAuth();

  let expiryText: string;

  if (deal?.endAt) {
    const { value, unit }: GetDateDiffType = getDateDiff(
      new Date(UTCtoLocalTime(deal.endAt)),
      new Date()
    );
    expiryText = ["second", "seconds"].includes(unit)
      ? translate("store_expires_within_title")
      : translate("store_expires_in_title", [
          translate(`num_${unit}`, [value]),
        ]);
  }

  const handleOnShowCodeClick = (
    deal: StoreDeal,
    isLoggedIn: boolean
  ): void => {
    onEventTrack && onEventTrack(GenzoCouponCardCtaType.ShowCodeButton);
    onShowCodeClick(deal, isLoggedIn);
  };

  if (!deal) return <></>;

  return (
    <div
      data-testid="coupon-card"
      className={clsx(styles.container, isRtl && styles.rtl)}
    >
      <div className={styles.content}>
        <div className={styles.logo}>
          <Image
            src={deal.store.logoUrl}
            alt="coupon store logo"
            layout="fill"
            objectFit="contain"
          />
        </div>

        <div className={styles.info}>
          <div className={styles.providerName}>{deal.store.name}</div>
          <div className={styles.title}>{deal.title}</div>
          <div className={styles.expiry}>{expiryText}</div>

          <div>
            <span className={styles.cashback}>{deal.store.cashbackText}</span>
            <span className={styles.previousCashback}>
              {deal.store.cashbackTextSuffix}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.button}>
        <ButtonWithPreview
          onClick={() => handleOnShowCodeClick(deal, !!user)}
          previewText={deal.couponCode}
        >
          {translate("show_code")}
        </ButtonWithPreview>
      </div>
    </div>
  );
};

export default CouponCard;
